import { useId } from "react";
import markSvg from "@manos/ui/brand/mark.svg?raw";
import wordSvg from "@manos/ui/brand/wordmark.svg?raw";

/**
 * The Manos logo, traced from the artwork (scripts/brand.cjs regenerates the SVGs). Drawn in
 * currentColor, so it takes the surrounding text color.
 */
const inline = (svg: string, className: string, label?: string) =>
  svg.replace("<svg ", `<svg class="${className}" ${label ? `role="img" aria-label="${label}"` : 'aria-hidden="true"'} focusable="false" `);

const MARK_VIEWBOX = markSvg.match(/viewBox="([^"]+)"/)![1]!;
const MARK_INNER = markSvg.replace(/^[\s\S]*?<svg[^>]*>/, "").replace(/<\/svg>\s*$/, "");
const [, , MARK_W, MARK_H] = MARK_VIEWBOX.split(/\s+/).map(Number) as [number, number, number, number];
const PLAYED = "logoPlayed";

/**
 * The mark. `animate` plays the greeting once per visit: the two hands swing in from the wrists and
 * their fingertips tap together twice. Each hand is the whole drawing clipped to its half (they meet
 * on the center line), so the artwork stays exactly as traced. Skipped for reduced-motion users.
 */
export function BrandMark({ className = "brand-mark", label, animate = false }: { className?: string; label?: string; animate?: boolean }) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");

  if (!animate) return <span style={{ display: "contents" }} dangerouslySetInnerHTML={{ __html: inline(markSvg, className, label) }} />;
  const half = MARK_W / 2;
  return (
    <svg className={`${className} brand-mark-animated`} viewBox={MARK_VIEWBOX} fill="currentColor" focusable="false" {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}>
      <defs>
        <g id={`${id}-m`} dangerouslySetInnerHTML={{ __html: MARK_INNER }} />
        <clipPath id={`${id}-l`}><rect x="0" y="0" width={half} height={MARK_H} /></clipPath>
        <clipPath id={`${id}-r`}><rect x={half} y="0" width={half} height={MARK_H} /></clipPath>
      </defs>
      <g className="brand-hand brand-hand-l" clipPath={`url(#${id}-l)`}><use href={`#${id}-m`} /></g>
      <g className="brand-hand brand-hand-r" clipPath={`url(#${id}-r)`}><use href={`#${id}-m`} /></g>
    </svg>
  );
}

/**
 * For the top of <head>, so it runs before anything paints. The first page load of a visit records
 * the visit and plays the intro; every later load hides it with a style rule added to <head>. No
 * timers and no dependence on React (which owns <html> and may reset its attributes), so a script
 * error or a quick click can't make the intro replay.
 */
export const LOGO_PLAYED_SCRIPT = `try{var k="${PLAYED}";if(sessionStorage.getItem(k)){var s=document.createElement("style");s.textContent=".intro{display:none!important}";document.head.appendChild(s);document.documentElement.dataset[k]="1"}else{sessionStorage.setItem(k,"1")}}catch(e){}`;

export function BrandWordmark({ className = "brand-word", label = "Manos" }: { className?: string; label?: string }) {
  return <span style={{ display: "contents" }} dangerouslySetInnerHTML={{ __html: inline(wordSvg, className, label) }} />;
}
